import { useState } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { adminGet } from "../../../api.ts";
import {
  resolveTrendGranularity,
  type TrendGranularity,
  type TrendRange,
} from "../../../components/StatsTrendControls.tsx";
import type { AnalyticsStatsResponse, ChatsResponse } from "./stats-shared.ts";

/** 与 API parseStatsSections 对齐的报表切片 */
export type StatsSection =
  | "overview"
  | "disposition"
  | "visitTrend"
  | "funnel"
  | "funnelByChat"
  | "conversionStatus"
  | "opsAlerts"
  | "topAds"
  | "countries"
  | "memberReconcile"
  | "untracked";

export const OVERVIEW_SECTIONS: ReadonlyArray<StatsSection> = [
  "overview",
  "disposition",
  "visitTrend",
  "funnel",
  "conversionStatus",
  "opsAlerts",
  "untracked",
];

export const CHAT_FUNNEL_SECTIONS: ReadonlyArray<StatsSection> = ["funnelByChat"];

export const MEMBER_RECONCILE_SECTIONS: ReadonlyArray<StatsSection> = ["memberReconcile"];

export const ADS_GEO_SECTIONS: ReadonlyArray<StatsSection> = ["topAds", "countries"];

export function useAnalyticsStats(
  botId: string,
  sections: ReadonlyArray<StatsSection> = OVERVIEW_SECTIONS,
) {
  const [range, setRange] = useState<TrendRange>("today");
  const [granularity, setGranularity] = useState<TrendGranularity>("hour");
  const [chatId, setChatId] = useState<string | null>(null);
  const [adIdInput, setAdIdInput] = useState("");
  const [debouncedAdId] = useDebouncedValue(adIdInput, 300);
  const adId = debouncedAdId.trim() === "" ? null : debouncedAdId.trim();

  const chatsQuery = useQuery({
    queryKey: ["analytics", botId, "chats"],
    queryFn: () => adminGet<ChatsResponse>(`/analytics/${botId}/chats`),
    staleTime: 60_000,
  });

  const chatOptions = (chatsQuery.data?.items ?? []).map((c) => ({
    value: String(c.chatId),
    label: c.title === null || c.title === "" ? String(c.chatId) : `${c.title} (${c.chatId})`,
  }));

  const sectionsKey = sections.join(",");
  const filterQs = [
    chatId === null ? null : `chatId=${encodeURIComponent(chatId)}`,
    adId === null ? null : `adId=${encodeURIComponent(adId)}`,
    `sections=${encodeURIComponent(sectionsKey)}`,
  ]
    .filter((s) => s !== null)
    .join("&");

  const requestGranularity = resolveTrendGranularity(range, granularity);

  const statsQuery = useQuery({
    queryKey: ["analytics", botId, "stats", range, requestGranularity, chatId, adId, sectionsKey],
    queryFn: () =>
      adminGet<AnalyticsStatsResponse>(
        `/analytics/${botId}/stats?range=${range}&granularity=${requestGranularity}&${filterQs}`,
      ),
    refetchInterval: 60_000,
  });

  const activeRange = statsQuery.data?.range ?? range;
  const activeGranularity = statsQuery.data?.granularity ?? granularity;

  return {
    range,
    setRange,
    granularity,
    setGranularity,
    chatId,
    setChatId,
    adIdInput,
    setAdIdInput,
    adId,
    chatOptions,
    chatsLoading: chatsQuery.isLoading,
    data: statsQuery.data,
    isLoading: statsQuery.isLoading,
    isError: statsQuery.isError,
    error: statsQuery.error,
    activeRange,
    activeGranularity,
  };
}

export type AnalyticsStatsHook = ReturnType<typeof useAnalyticsStats>;
